//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * Dialog Component
 *
 * A reusable modal dialog component for displaying content in an overlay.
 * Wraps Radix UI's Dialog primitive and provides consistent styling and behavior.
 * Includes overlay, content area, and close functionality.
 *
 * Usage:
 * <Dialog>
 *   <DialogTrigger>Open Dialog</DialogTrigger>
 *   <DialogContent>
 *     <h2>Dialog Title</h2>
 *     <p>Dialog content goes here</p>
 *     <DialogClose>Close</DialogClose>
 *   </DialogContent>
 * </Dialog>
 */

import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from './utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogTitle = DialogPrimitive.Title;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-[1300] bg-black/50', className)}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-[1300] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg outline-none',
        className,
      )}
      {...props}
    />
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

export { Dialog, DialogTrigger, DialogContent, DialogClose, DialogTitle };
